import { diffLines, diffChars, type ChangeObject } from "diff";
import { I18n } from "@/i18n/i18n";
import { escapeHtml } from "@/utilities";

type LineOp =
    | { kind: "equal"; genLineNr: number; expLineNr: number; line: string }
    | { kind: "modified"; genLineNr: number; expLineNr: number; delLine: string; insLine: string }
    | { kind: "removed"; lineNr: number; line: string }
    | { kind: "added"; lineNr: number; line: string };

/* Walks diffLines() chunks and converts them into per-line operations, pairing
 * adjacent removed/added chunks line-by-line so modified rows can be rendered
 * with per-char highlighting. Shared between the split and unified renderers. */
function pairLineChanges(changes: ChangeObject<string>[]): LineOp[] {
    const ops: LineOp[] = [];
    let genLineNr = 1;
    let expLineNr = 1;
    let pendingDel: string[] = [];
    let pendingDelStart = 0;
    let pendingIns: string[] = [];
    let pendingInsStart = 0;

    function flush(): void {
        const maxLen = Math.max(pendingDel.length, pendingIns.length);
        for (let i = 0; i < maxLen; i++) {
            const delLine = pendingDel[i];
            const insLine = pendingIns[i];
            if (delLine !== undefined && insLine !== undefined) {
                ops.push({
                    kind: "modified",
                    genLineNr: pendingDelStart + i + 1,
                    expLineNr: pendingInsStart + i + 1,
                    delLine,
                    insLine,
                });
            } else if (delLine !== undefined) {
                ops.push({ kind: "removed", lineNr: pendingDelStart + i + 1, line: delLine });
            } else {
                ops.push({ kind: "added", lineNr: pendingInsStart + i + 1, line: insLine });
            }
        }
        genLineNr += pendingDel.length;
        expLineNr += pendingIns.length;
        pendingDel = [];
        pendingIns = [];
    }

    for (const change of changes) {
        const rawLines = change.value.split("\n");
        if (rawLines[rawLines.length - 1] === "") rawLines.pop();

        if (!change.added && !change.removed) {
            flush();
            for (const line of rawLines) {
                ops.push({ kind: "equal", genLineNr: genLineNr++, expLineNr: expLineNr++, line });
            }
        } else if (change.removed) {
            if (pendingIns.length > 0) flush();
            pendingDelStart = genLineNr - 1;
            pendingDel = rawLines;
        } else {
            pendingInsStart = expLineNr - 1;
            pendingIns = rawLines;
            flush();
        }
    }
    flush();
    return ops;
}

const CHAR_THRESHOLD = 100;

// Trailing \n doesn't produce an extra empty line (e.g. "a\nb\n" → 2 lines, not 3).
function countLines(s: string): number {
    return rubyLines(s).length;
}

function rubyLines(s: string): string[] {
    return s.match(/[^\n]*\n|[^\n]+/g) ?? [];
}

/* Diffs two strings char-by-char and wraps changed characters in <strong>.
 * Falls back to plain escaping when either string exceeds CHAR_THRESHOLD —
 * char-level diffs on very long lines produce too much markup to be useful. */
function diffStrings(generated: string, expected: string): [string, string] {
    if (generated.length > CHAR_THRESHOLD || expected.length > CHAR_THRESHOLD) {
        return [escapeHtml(generated), escapeHtml(expected)];
    }

    const changes = diffChars(generated, expected);
    let genResult = "";
    let expResult = "";
    let inGenStrong = false;
    let inExpStrong = false;

    for (const change of changes) {
        if (change.removed) {
            if (!inGenStrong) {
                genResult += "<strong>";
                inGenStrong = true;
            }
            genResult += escapeHtml(change.value);
        } else if (change.added) {
            if (!inExpStrong) {
                expResult += "<strong>";
                inExpStrong = true;
            }
            expResult += escapeHtml(change.value);
        } else {
            if (inGenStrong) {
                genResult += "</strong>";
                inGenStrong = false;
            }
            if (inExpStrong) {
                expResult += "</strong>";
                inExpStrong = false;
            }
            genResult += escapeHtml(change.value);
            expResult += escapeHtml(change.value);
        }
    }

    if (inGenStrong) genResult += "</strong>";
    if (inExpStrong) expResult += "</strong>";

    return [genResult, expResult];
}

export interface DiffTables {
    split: string;
    unified: string;
    cutoffWarning?: string;
}

/**
 * Computes split and unified diff tables for the given generated/expected strings.
 * Truncates generated output that vastly exceeds the expected length before diffing
 * to keep table size manageable, and attaches a cutoff warning when it does so.
 */
export function computeTextDiff(rawGenerated: string, rawExpected: string): DiffTables {
    let generated = rawGenerated ?? "";
    const expected = rawExpected ?? "";
    let tooManyCharacters = 0;
    let tooManyLines = 0;

    // Truncate if generated output is more than 5000 chars longer than expected.
    if (generated.length > expected.length + 5000) {
        tooManyCharacters = generated.length;
        generated = generated.slice(0, expected.length + 5000) + "...";
    }

    // Truncate if generated output has more than 100 extra lines beyond expected.
    const expRubyLines = rubyLines(expected);
    let genRubyLines = rubyLines(generated);
    if (genRubyLines.length > expRubyLines.length + 100) {
        tooManyLines = genRubyLines.length;
        generated = `${genRubyLines.slice(0, expRubyLines.length + 100).join("")}...\n`;
        genRubyLines = rubyLines(generated);
    }

    const finalGenLines = generated.split("\n");
    const finalExpLines = expected.split("\n");
    const generatedLineCount = genRubyLines.length;
    const expectedLineCount = expRubyLines.length;
    const simplified = generatedLineCount > 100 || expectedLineCount > 100;

    const cutoffWarning = buildCutoffWarning(tooManyCharacters, tooManyLines, expected);

    // diffLines is the expensive step; compute once and share between both views.
    const ops: LineOp[] = simplified
        ? []
        : pairLineChanges(diffLines(finalGenLines.join("\n"), finalExpLines.join("\n"), { newlineIsToken: false }));

    return {
        split: buildSplitTable(generated, expected, ops, generatedLineCount, expectedLineCount, simplified),
        unified: buildUnifiedTable(generated, expected, ops, generatedLineCount, expectedLineCount, simplified),
        cutoffWarning,
    };
}

// ---------------------------------------------------------------------------
// Split table
// ---------------------------------------------------------------------------

function buildSplitTable(
    generated: string,
    expected: string,
    ops: LineOp[],
    generatedLineCount: number,
    expectedLineCount: number,
    simplified: boolean,
): string {
    const yourOutput = escapeHtml(I18n.t("js.submissions_show.your_output"));
    const expectedLabel = escapeHtml(I18n.t("js.submissions_show.expected"));
    const lineNumber = escapeHtml(I18n.t("js.submissions_show.line_number"));

    const rows = simplified
        ? buildSimpleSplitRows(generated, expected, generatedLineCount, expectedLineCount)
        : buildDiffSplitRows(ops);

    return `<table class="split-diff diff">
<colgroup><col class="line-nr"><col class="del-output"><col class="line-nr"><col class="ins-output"></colgroup>
<thead><tr>
<th scope="col" class="line-nr" title="${yourOutput}"><i class="mdi mdi-18 mdi-file-account" aria-hidden="true"></i><span class="visually-hidden">${lineNumber}</span></th>
<th scope="col">${yourOutput}</th>
<th scope="col" class="line-nr" title="${expectedLabel}"><i class="mdi mdi-18 mdi-file-check" aria-hidden="true"></i><span class="visually-hidden">${lineNumber}</span></th>
<th scope="col">${expectedLabel}</th>
</tr></thead>
<tbody>${rows}</tbody>
</table>`;
}

function buildSimpleSplitRows(
    generated: string,
    expected: string,
    generatedLineCount: number,
    expectedLineCount: number,
): string {
    const genNrs = Array.from({ length: generatedLineCount }, (_, i) => i + 1).join("\n");
    const expNrs = Array.from({ length: expectedLineCount }, (_, i) => i + 1).join("\n");
    return `<tr>
<td class="line-nr">${genNrs}</td>
<td class="del">${escapeHtml(generated)}</td>
<td class="line-nr">${expNrs}</td>
<td class="ins">${escapeHtml(expected)}</td>
</tr>`;
}

function buildDiffSplitRows(ops: LineOp[]): string {
    let rows = "";
    for (const op of ops) {
        if (op.kind === "equal") {
            rows += `<tr>
<td class="line-nr">${op.genLineNr}</td>
<td class="unchanged">${escapeHtml(op.line)}</td>
<td class="line-nr">${op.expLineNr}</td>
<td class="unchanged">${escapeHtml(op.line)}</td>
</tr>`;
        } else if (op.kind === "modified") {
            const [genHtml, expHtml] = diffStrings(op.delLine, op.insLine);
            rows += `<tr>
<td class="line-nr">${op.genLineNr}</td>
<td class="del">${genHtml}</td>
<td class="line-nr">${op.expLineNr}</td>
<td class="ins">${expHtml}</td>
</tr>`;
        } else if (op.kind === "removed") {
            rows += `<tr>
<td class="line-nr">${op.lineNr}</td>
<td class="del">${escapeHtml(op.line)}</td>
<td class="line-nr"></td>
<td></td>
</tr>`;
        } else {
            rows += `<tr>
<td class="line-nr"></td>
<td></td>
<td class="line-nr">${op.lineNr}</td>
<td class="ins">${escapeHtml(op.line)}</td>
</tr>`;
        }
    }
    return rows;
}

// ---------------------------------------------------------------------------
// Unified table
// ---------------------------------------------------------------------------

function buildUnifiedTable(
    generated: string,
    expected: string,
    ops: LineOp[],
    generatedLineCount: number,
    expectedLineCount: number,
    simplified: boolean,
): string {
    const yourOutput = escapeHtml(I18n.t("js.submissions_show.your_output"));
    const expectedLabel = escapeHtml(I18n.t("js.submissions_show.expected"));
    const lineNumber = escapeHtml(I18n.t("js.submissions_show.line_number"));
    const outputLabel = escapeHtml(I18n.t("js.submissions_show.output"));

    const rows = simplified
        ? buildSimpleUnifiedRows(generated, expected, generatedLineCount, expectedLineCount)
        : buildDiffUnifiedRows(ops);

    return `<table class="unified-diff diff">
<colgroup><col class="line-nr"><col class="line-nr"><col class="output"></colgroup>
<thead><tr>
<th scope="col" class="line-nr" title="${yourOutput}"><i class="mdi mdi-18 mdi-file-account" aria-hidden="true"></i><span class="visually-hidden">${lineNumber}</span></th>
<th scope="col" class="line-nr" title="${expectedLabel}"><i class="mdi mdi-18 mdi-file-check" aria-hidden="true"></i><span class="visually-hidden">${lineNumber}</span></th>
<th scope="col">${outputLabel}</th>
</tr></thead>
<tbody>${rows}</tbody>
</table>`;
}

function buildSimpleUnifiedRows(
    generated: string,
    expected: string,
    generatedLineCount: number,
    expectedLineCount: number,
): string {
    const genNrs = Array.from({ length: generatedLineCount }, (_, i) => i + 1).join("\n");
    const expNrs = Array.from({ length: expectedLineCount }, (_, i) => i + 1).join("\n");
    return `<tr>
<td class="line-nr">${genNrs}</td>
<td class="line-nr"></td>
<td class="del">${escapeHtml(generated)}</td>
</tr>
<tr>
<td class="line-nr"></td>
<td class="line-nr">${expNrs}</td>
<td class="ins">${escapeHtml(expected)}</td>
</tr>`;
}

function buildDiffUnifiedRows(ops: LineOp[]): string {
    let rows = "";
    for (const op of ops) {
        if (op.kind === "equal") {
            rows += `<tr>
<td class="line-nr">${op.genLineNr}</td>
<td class="line-nr">${op.expLineNr}</td>
<td class="unchanged">${escapeHtml(op.line)}</td>
</tr>`;
        } else if (op.kind === "modified") {
            const [genHtml, expHtml] = diffStrings(op.delLine, op.insLine);
            rows += `<tr>
<td class="line-nr">${op.genLineNr}</td>
<td class="line-nr"></td>
<td class="del">${genHtml}</td>
</tr>
<tr>
<td class="line-nr"></td>
<td class="line-nr">${op.expLineNr}</td>
<td class="ins">${expHtml}</td>
</tr>`;
        } else if (op.kind === "removed") {
            rows += `<tr>
<td class="line-nr">${op.lineNr}</td>
<td class="line-nr"></td>
<td class="del">${escapeHtml(op.line)}</td>
</tr>`;
        } else {
            rows += `<tr>
<td class="line-nr"></td>
<td class="line-nr">${op.lineNr}</td>
<td class="ins">${escapeHtml(op.line)}</td>
</tr>`;
        }
    }
    return rows;
}

// ---------------------------------------------------------------------------
// Cutoff warning
// ---------------------------------------------------------------------------

function buildCutoffWarning(tooManyCharacters: number, tooManyLines: number, expected: string): string | undefined {
    if (tooManyCharacters) {
        const count = expected.length;
        const msg = I18n.t("js.submissions_show.cut_off_message", {
            generated: tooManyCharacters,
            smart_count: count,
        });
        return `<div class="callout callout-warning">${msg}</div>`;
    }
    if (tooManyLines) {
        const count = countLines(expected);
        const msg = I18n.t("js.submissions_show.cut_off_message_lines", {
            generated: tooManyLines,
            smart_count: count,
        });
        return `<div class="callout callout-warning">${msg}</div>`;
    }
    return undefined;
}
